<script setup>
import { defineEmits, defineProps, onBeforeMount } from "vue";
import { useI18n } from "vue-i18n";
import { ref, reactive, computed } from 'vue';
import { to } from "await-to-js";
import { interceptor, useEnvStore, useTwoWayBinding } from "@dutypay/front-end-design-system";
import { useOneTimePasswordValidation } from "@dutypay/front-end-design-system/src/validations";

const props = defineProps({
  modelValue: { type: Object, required: true }
});
const notification = reactive({
  sendSMS: {
    success: false,
    asyncError: false
  }
});
const isLoadingAuthSMS = ref(false);
const countdown = ref(0);
const { t } = useI18n();
const emit = defineEmits(["update", "trigger-authenticate"]);
const loginData = useTwoWayBinding(props, emit, "modelValue");
const isResendDisabled = ref(false);
const envStore = useEnvStore();

async function verifySMSAuthentication() {
  loginData.value.mode = "sms";
  emit("trigger-authenticate");
}

const mobileNumberCensored = ref("");
const phraseWithMobileNumber = computed(() => {
  return t("sso.pages.LoginStages.SMSOTPForm.unlockWith.label", { mobileNumberPlaceholder: `<bdo dir="ltr">${mobileNumberCensored}</bdo>`, });
});

function countDownTimer() {
  if (countdown.value > 0) {
    setTimeout(() => {
      countdown.value -= 1;
      countDownTimer();
    }, 1000);
  } else {
    isResendDisabled.value = false;
  }
}

async function sendAuthenticationSMS() {
  isResendDisabled.value = true;
  isLoadingAuthSMS.value = true;
  notification.sendSMS.success = false;
  notification.sendSMS.asyncError = false;
  let err, smsResponse;
  [err, smsResponse] = await to(interceptor.post(`${envStore.apiUrls.dutypay}/api/v0/send/auth-sms`, {
      ...loginData.value
    }));
  isLoadingAuthSMS.value = false;
  if (err) {
    isResendDisabled.value = false;
    notification.sendSMS.success = false;
    notification.sendSMS.asyncError = true;
    throw err;
  }

  if (smsResponse.status === 200) {
    mobileNumberCensored.value = smsResponse?.data?.result?.mobileNumberCensored;
  }
  notification.sendSMS.success = true;
  notification.sendSMS.asyncError = false;
  countdown.value = 20;
  countDownTimer();
}
onBeforeMount(async () => {
  loginData.value.SMSOTP = "";
  mobileNumberCensored.value = "";
  await sendAuthenticationSMS();
});

const validation = {
  SMSOTP: [
    useOneTimePasswordValidation()
  ],
}
</script>
<script>

export default {
  name: "SMSOTPForm"
};
</script>

<template>
  <div>
    <h2>{{ t("sso.pages.LoginStages.SMSOTPForm.SMSOTPForm.title") }}</h2>

    <DpNotification
      :visibility="notification.sendSMS.success"
      :title="t('sso.pages.LoginStages.SMSOTPForm.notification.sendSMS.success.title')"
      :message="t('sso.pages.LoginStages.SMSOTPForm.notification.sendSMS.success.message', { mobileNumberPlaceholder: mobileNumberCensored })"
      type="success">
      <template #controls>
        <dp-button
          purpose="secondaryAction"
          class="full-width"
          :disabled="isResendDisabled"
          @click="sendAuthenticationSMS">
          <span v-if="isResendDisabled">{{
              t("sso.pages.LoginStages.SMSOTPForm.resendSMSInSeconds.buttonLabel", { secondsPlaceholder: countdown })
            }}</span>
          <span v-else>{{ t("sso.pages.LoginStages.SMSOTPForm.resendSMS.buttonLabel") }}</span>
        </dp-button>
      </template>
    </DpNotification>

    <DpNotification
      :visibility="notification.sendSMS.asyncError"
      :title="t('sso.pages.LoginStages.SMSOTPForm.notification.sendSMS.asyncError.title')"
      :message="t('sso.pages.LoginStages.SMSOTPForm.notification.sendSMS.asyncError.message')"
      type="error"
    ></DpNotification>

    <p v-html="phraseWithMobileNumber"></p>

    <DpForm
      v-model="loginData"
      v-loading="isLoadingAuthSMS"
      @submit="verifySMSAuthentication"
      :showValidationNotification="false"
      :validation="validation"
      >
      <DpInput
      v-model="loginData.SMSOTP"
      :validation="validation.SMSOTP"
      id="SMSOTP"
      autocomplete="off"
      :label="t('sso.pages.LoginStages.SMSOTPForm.SMSOTP.input.label')"
      :placeholder="t('sso.pages.LoginStages.SMSOTPForm.SMSOTP.input.placeholder')" />

      <template #control="{ validate }">
        <DpButton
          purpose="primaryAction"
          size="large"
          @click="validate">
          {{ t("sso.pages.LoginStages.SMSOTPForm.confirmCode.buttonLabel") }}
        </DpButton>
      </template>
    </DpForm>
  </div>
</template>

<i18n>
{
  "ar-AE": {
    "notification": {
      "sendSMS": {
        "success": {
          "message": "تم إرسال رمز التوثيق عبر الرسائل القصيرة إلى {mobileNumberPlaceholder}.",
          "title": "عمل ناجح"
        },
        "asyncError": {
          "message": "تعذر إرسال الرسالة. يرجى التحقق من دخولك.",
          "title": "خطأ"
        }
      }
    },
    "smsSent": {
      "notificationPersist": {
        "success": {
          "title": "عمل ناجح",
          "message": "تم إرسال رمز المصادقة عبر الرسائل القصيرة. يمكن استخدامه مرة واحدة فقط وضمن فترة زمنية محدودة لتسجيل الدخول."
        }
      }
    },
    "SMSOTPForm": {
      "title": "رمز المصادقة عبر الرسائل القصيرة"
    },
    "resendSMS": {
      "buttonLabel": "أرسل مرة أخرى"
    },
    "unlockWith": {
      "label": "أدخل وتأكد من استلام رمز المصادقة."
    },
    "confirmCode": {
      "buttonLabel": "تأكيد"
    },
    "SMSOTP": {
      "input": {
        "placeholder": "أدخل رمز التحقق"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "إعادة الإرسال في {secondsPlaceholder} ثوان"
    }
  },
  "ar-DZ": {
    "notification": {
      "sendSMS": {
        "success": {
          "message": "تم إرسال رمز التوثيق عبر الرسائل القصيرة إلى {mobileNumberPlaceholder}.",
          "title": "عمل ناجح"
        },
        "asyncError": {
          "title": "خطأ"
        }
      }
    },
    "smsSent": {
      "notificationPersist": {
        "success": {}
      }
    },
    "SMSOTPForm": {},
    "resendSMS": {
      "buttonLabel": "أرسل مرة أخرى"
    },
    "unlockWith": {
      "label": "أدخل وتأكد من استلام رمز المصادقة."
    },
    "confirmCode": {
      "buttonLabel": "تأكيد"
    },
    "SMSOTP": {
      "input": {
        "placeholder": "أدخل رمز التحقق"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "إعادة الإرسال في {secondsPlaceholder} ثوان"
    }
  },
  "ar-EG": {
    "notification": {
      "sendSMS": {
        "success": {
          "message": "تم إرسال رمز التوثيق عبر الرسائل القصيرة إلى {mobileNumberPlaceholder}.",
          "title": "عمل ناجح"
        },
        "asyncError": {
          "message": "تعذر إرسال الرسالة. يرجى التحقق من دخولك.",
          "title": "خطأ"
        }
      }
    },
    "smsSent": {
      "notificationPersist": {
        "success": {
          "title": "عمل ناجح",
          "message": "تم إرسال رمز المصادقة عبر الرسائل القصيرة. يمكن استخدامه مرة واحدة فقط وضمن فترة زمنية محدودة لتسجيل الدخول."
        }
      }
    },
    "SMSOTPForm": {
      "title": "رمز المصادقة عبر الرسائل القصيرة"
    },
    "resendSMS": {
      "buttonLabel": "أرسل مرة أخرى"
    },
    "unlockWith": {
      "label": "أدخل وتأكد من استلام رمز المصادقة."
    },
    "confirmCode": {
      "buttonLabel": "تأكيد"
    },
    "SMSOTP": {
      "input": {
        "placeholder": "أدخل رمز التحقق"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "إعادة الإرسال في {secondsPlaceholder} ثوان"
    }
  },
  "ar-LB": {
    "notification": {
      "sendSMS": {
        "success": {
          "message": "تم إرسال رمز التوثيق عبر الرسائل القصيرة إلى {mobileNumberPlaceholder}.",
          "title": "عمل ناجح"
        },
        "asyncError": {
          "title": "خطأ"
        }
      }
    },
    "smsSent": {
      "notificationPersist": {
        "success": {}
      }
    },
    "SMSOTPForm": {},
    "resendSMS": {
      "buttonLabel": "أرسل مرة أخرى"
    },
    "unlockWith": {
      "label": "أدخل وتأكد من استلام رمز المصادقة."
    },
    "confirmCode": {
      "buttonLabel": "تأكيد"
    },
    "SMSOTP": {
      "input": {
        "placeholder": "أدخل رمز التحقق"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "إعادة الإرسال في {secondsPlaceholder} ثواني"
    }
  },
  "ar-MA": {
    "notification": {
      "sendSMS": {
        "success": {
          "message": "تم إرسال رمز التوثيق عبر الرسائل القصيرة إلى {mobileNumberPlaceholder}.",
          "title": "عمل ناجح"
        },
        "asyncError": {
          "title": "خطأ"
        }
      }
    },
    "smsSent": {
      "notificationPersist": {
        "success": {}
      }
    },
    "SMSOTPForm": {},
    "resendSMS": {
      "buttonLabel": "أرسل مرة أخرى"
    },
    "unlockWith": {
      "label": "أدخل وتأكد من استلام رمز المصادقة."
    },
    "confirmCode": {
      "buttonLabel": "تأكيد"
    },
    "SMSOTP": {
      "input": {
        "placeholder": "ادخل رمز التأكيد"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "إعادة الإرسال في {secondsPlaceholder} ثوان"
    }
  },
  "ar-SA": {
    "notification": {
      "sendSMS": {
        "success": {
          "message": "تم إرسال رمز التوثيق عبر الرسائل القصيرة إلى {mobileNumberPlaceholder}.",
          "title": "عمل ناجح"
        },
        "asyncError": {
          "message": "تعذر إرسال الرسالة. يرجى التحقق من دخولك.",
          "title": "خطأ"
        }
      }
    },
    "smsSent": {
      "notificationPersist": {
        "success": {
          "title": "عمل ناجح",
          "message": "تم إرسال رمز المصادقة عبر الرسائل القصيرة. يمكن استخدامه مرة واحدة فقط وضمن فترة زمنية محدودة لتسجيل الدخول."
        }
      }
    },
    "SMSOTPForm": {
      "title": "رمز المصادقة عبر الرسائل القصيرة"
    },
    "resendSMS": {
      "buttonLabel": "أعد الإرسال"
    },
    "unlockWith": {
      "label": "أدخل وتأكد من استلام رمز المصادقة."
    },
    "confirmCode": {
      "buttonLabel": "تأكيد"
    },
    "SMSOTP": {
      "input": {
        "placeholder": "أدخل رمز التحقق"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "إعادة الإرسال في {secondsPlaceholder} ثوان"
    }
  },
  "ar-SY": {
    "notification": {
      "sendSMS": {
        "success": {
          "message": "تم إرسال رمز التوثيق عبر الرسائل القصيرة إلى {mobileNumberPlaceholder}.",
          "title": "عمل ناجح"
        },
        "asyncError": {
          "title": "خطأ"
        }
      }
    },
    "smsSent": {
      "notificationPersist": {
        "success": {}
      }
    },
    "SMSOTPForm": {},
    "resendSMS": {
      "buttonLabel": "أرسل مرة أخرى"
    },
    "unlockWith": {
      "label": "أدخل وتأكد من استلام رمز المصادقة."
    },
    "confirmCode": {
      "buttonLabel": "تأكيد"
    },
    "SMSOTP": {
      "input": {
        "placeholder": "أدخل رمز التحقق"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "إعادة الإرسال في {secondsPlaceholder} ثوان"
    }
  },
  "ar-TN": {
    "notification": {
      "sendSMS": {
        "success": {
          "message": "تم إرسال رمز التوثيق عبر الرسائل القصيرة إلى {mobileNumberPlaceholder}.",
          "title": "عمل ناجح"
        },
        "asyncError": {
          "title": "خطأ"
        }
      }
    },
    "smsSent": {
      "notificationPersist": {
        "success": {}
      }
    },
    "SMSOTPForm": {},
    "resendSMS": {
      "buttonLabel": "أرسل مرة أخرى"
    },
    "unlockWith": {
      "label": "أدخل وتأكد من استلام رمز المصادقة."
    },
    "confirmCode": {
      "buttonLabel": "تأكيد"
    },
    "SMSOTP": {
      "input": {
        "placeholder": "أدخل رمز التحقق"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "إعادة الإرسال في {secondsPlaceholder} ثواني"
    }
  },
  "bg-BG": {
    "notification": {
      "sendSMS": {
        "success": {
          "message": "Кодът за удостоверяване беше изпратен чрез СМС до {mobileNumberPlaceholder}.",
          "title": "Успешно действие"
        },
        "asyncError": {
          "message": "Съобщението не можа да бъде изпратено. Моля, проверете вашето въвеждане.",
          "title": "грешка"
        }
      }
    },
    "smsSent": {
      "notificationPersist": {
        "success": {
          "title": "Успешно действие",
          "message": "Кодът за удостоверяване беше изпратен чрез SMS. Може да се използва само веднъж и в рамките на ограничено време за влизане."
        }
      }
    },
    "SMSOTPForm": {
      "title": "Код за удостоверяване чрез SMS"
    },
    "resendSMS": {
      "buttonLabel": "Изпрати отново"
    },
    "unlockWith": {
      "label": "Въведете и потвърдете получения код за удостоверяване."
    },
    "confirmCode": {
      "buttonLabel": "Потвърждение"
    },
    "SMSOTP": {
      "input": {
        "placeholder": "Въведете код за удостоверяване"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "Преизпрати след {secondsPlaceholder} секунди"
    }
  },
  "bs-BA": {
    "notification": {
      "sendSMS": {
        "success": {
          "message": "Autentifikacijski kod je poslan putem SMS-a na {mobileNumberPlaceholder}.",
          "title": "Uspješno obavljeno"
        },
        "asyncError": {
          "message": "Poruka se nije mogla poslati. Molimo provjerite svoj unos.",
          "title": "Greška"
        }
      }
    },
    "smsSent": {
      "notificationPersist": {
        "success": {
          "title": "Uspješno obavljeno",
          "message": "Kod za autentifikaciju je poslan putem SMS-a. Može se koristiti samo jednom iu ograničenom vremenu za prijavu."
        }
      }
    },
    "SMSOTPForm": {
      "title": "Autentifikacijski kod putem SMS-a"
    },
    "resendSMS": {
      "buttonLabel": "Pošalji ponovo"
    },
    "unlockWith": {
      "label": "Unesite i potvrdite primljeni kod za autentifikaciju."
    },
    "confirmCode": {
      "buttonLabel": "Potvrdite"
    },
    "SMSOTP": {
      "input": {
        "placeholder": "Unesite kod za autentifikaciju"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "Ponovno pošalji za {secondsPlaceholder} sekundi"
    }
  },
  "cs-CZ": {
    "notification": {
      "sendSMS": {
        "success": {
          "message": "Ověřovací kód byl odeslán pomocí SMS na {mobileNumberPlaceholder}.",
          "title": "Úspěšná akce"
        },
        "asyncError": {
          "message": "Zprávu se nepodařilo odeslat. Zkontrolujte prosím svůj údaj.",
          "title": "Chyba"
        }
      }
    },
    "smsSent": {
      "notificationPersist": {
        "success": {
          "title": "Úspěšná akce",
          "message": "Ověřovací kód byl zaslán prostřednictvím SMS. Lze jej použít pouze jednou a po omezenou dobu pro přihlášení."
        }
      }
    },
    "SMSOTPForm": {
      "title": "Ověřovací kód prostřednictvím SMS"
    },
    "resendSMS": {
      "buttonLabel": "Poslat znovu"
    },
    "unlockWith": {
      "label": "Zadejte a potvrďte přijatý ověřovací kód."
    },
    "confirmCode": {
      "buttonLabel": "Potvrdit"
    },
    "SMSOTP": {
      "input": {
        "placeholder": "Zadejte ověřovací kód"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "Znovu odeslat za {secondsPlaceholder} sekundy"
    }
  },
  "da-DK": {
    "notification": {
      "sendSMS": {
        "success": {
          "message": "Godkendelseskoden blev sendt via SMS til {mobileNumberPlaceholder}.",
          "title": "Vellykket handling"
        },
        "asyncError": {
          "message": "Meddelelsen kunne ikke sendes. Tjek venligst din post.",
          "title": "Fejl"
        }
      }
    },
    "smsSent": {
      "notificationPersist": {
        "success": {
          "title": "Vellykket handling",
          "message": "Godkendelseskoden blev sendt via SMS. Det kan kun bruges én gang og inden for en begrænset tid til at logge ind."
        }
      }
    },
    "SMSOTPForm": {
      "title": "Godkendelseskode via SMS"
    },
    "resendSMS": {
      "buttonLabel": "Send igen"
    },
    "unlockWith": {
      "label": "Indtast og bekræft den modtagne godkendelseskode."
    },
    "confirmCode": {
      "buttonLabel": "Bekræft"
    },
    "SMSOTP": {
      "input": {
        "placeholder": "Indtast godkendelseskode"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "Gensend om {secondsPlaceholder} sekunder"
    }
  },
  "de-DE": {
    "notification": {
      "sendSMS": {
        "success": {
          "message": "Der Authentifizierungscode wurde per SMS an {mobileNumberPlaceholder} gesendet.",
          "title": "Erfolgreiche Aktion"
        },
        "asyncError": {
          "message": "Die Nachricht konnte nicht gesendet werden. Bitte prüfe deine Eingabe.",
          "title": "Fehler"
        }
      }
    },
    "smsSent": {
      "notificationPersist": {
        "success": {
          "title": "Erfolgreiche Aktion",
          "message": "Der Authentifizierungscode wurde per SMS gesendet. Er kann nur einmalig und innerhalb einer begrenzten Zeit für die Anmeldung verwendet werden."
        }
      }
    },
    "SMSOTPForm": {
      "title": "Authentifizierungscode per SMS"
    },
    "resendSMS": {
      "buttonLabel": "Erneut senden"
    },
    "unlockWith": {
      "label": "Empfangenen Authentifizierungscode eingeben und bestätigen."
    },
    "confirmCode": {
      "buttonLabel": "Bestätigen"
    },
    "SMSOTP": {
      "input": {
        "placeholder": "Authentifizierungscode eingeben"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "Erneut senden in {secondsPlaceholder} Sekunden"
    }
  },
  "de-AT": {
    "notification": {
      "sendSMS": {
        "success": {
          "message": "Der Authentifizierungscode wurde per SMS an {mobileNumberPlaceholder} gesendet.",
          "title": "Erfolgreiche Aktion"
        },
        "asyncError": {
          "message": "Die Nachricht konnte nicht gesendet werden. Bitte prüfe deine Eingabe.",
          "title": "Fehler"
        }
      }
    },
    "smsSent": {
      "notificationPersist": {
        "success": {
          "title": "Erfolgreiche Aktion",
          "message": "Der Authentifizierungscode wurde per SMS gesendet. Er kann nur einmalig und innerhalb einer begrenzten Zeit für die Anmeldung verwendet werden."
        }
      }
    },
    "SMSOTPForm": {
      "title": "Authentifizierungscode per SMS"
    },
    "resendSMS": {
      "buttonLabel": "Erneut senden"
    },
    "unlockWith": {
      "label": "Empfangenen Authentifizierungscode eingeben und bestätigen."
    },
    "confirmCode": {
      "buttonLabel": "Bestätigen"
    },
    "SMSOTP": {
      "input": {
        "placeholder": "Authentifizierungscode eingeben"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "Erneut senden in {secondsPlaceholder} Sekunden"
    }
  },
  "de-CH": {
    "notification": {
      "sendSMS": {
        "success": {
          "message": "Der Authentifizierungscode wurde per SMS an {mobileNumberPlaceholder} gesendet.",
          "title": "Erfolgreiche Aktion"
        },
        "asyncError": {
          "message": "Die Nachricht konnte nicht gesendet werden. Bitte prüfe deine Eingabe.",
          "title": "Fehler"
        }
      }
    },
    "smsSent": {
      "notificationPersist": {
        "success": {
          "title": "Erfolgreiche Aktion",
          "message": "Der Authentifizierungscode wurde per SMS gesendet. Er kann nur einmalig und innerhalb einer begrenzten Zeit für die Anmeldung verwendet werden."
        }
      }
    },
    "SMSOTPForm": {
      "title": "Authentifizierungscode per SMS"
    },
    "resendSMS": {
      "buttonLabel": "Erneut senden"
    },
    "unlockWith": {
      "label": "Empfangenen Authentifizierungscode eingeben und bestätigen."
    },
    "confirmCode": {
      "buttonLabel": "Bestätigen"
    },
    "SMSOTP": {
      "input": {
        "placeholder": "Authentifizierungscode eingeben"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "Erneut senden in {secondsPlaceholder} Sekunden"
    }
  },
  "de-LU": {
    "notification": {
      "sendSMS": {
        "success": {
          "message": "Der Authentifizierungscode wurde per SMS an mobileNumberPlaceholder gesendet.",
          "title": "Erfolgreiche Aktion"
        },
        "asyncError": {
          "title": "Fehler"
        }
      }
    },
    "smsSent": {
      "notificationPersist": {
        "success": {}
      }
    },
    "SMSOTPForm": {},
    "resendSMS": {
      "buttonLabel": "Erneut senden"
    },
    "unlockWith": {
      "label": "Empfangenen Authentifizierungscode eingeben und bestätigen."
    },
    "confirmCode": {
      "buttonLabel": "Bestätigen"
    },
    "SMSOTP": {
      "input": {
        "placeholder": "Authentifizierungscode eingeben"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "Erneut senden in {secondsPlaceholder} Sekunden"
    }
  },
  "el-GR": {
    "notification": {
      "sendSMS": {
        "success": {
          "message": "Ο κωδικός επιβεβαίωσης στάλθηκε μέσω SMS στον {mobileNumberPlaceholder}.",
          "title": "Επιτυχημένη ενέργεια"
        },
        "asyncError": {
          "message": "Δεν ήταν δυνατή η αποστολή του μηνύματος. Ελέγξτε την καταχώρισή σας.",
          "title": "Σφάλμα"
        }
      }
    },
    "smsSent": {
      "notificationPersist": {
        "success": {
          "title": "Επιτυχημένη ενέργεια",
          "message": "Ο κωδικός ελέγχου ταυτότητας στάλθηκε μέσω SMS. Μπορεί να χρησιμοποιηθεί μόνο μία φορά και εντός περιορισμένου χρόνου για τη σύνδεση."
        }
      }
    },
    "SMSOTPForm": {
      "title": "Κωδικός ελέγχου ταυτότητας μέσω SMS"
    },
    "resendSMS": {
      "buttonLabel": "Στείλε ξανά"
    },
    "unlockWith": {
      "label": "Εισαγάγετε και επιβεβαιώστε τον κωδικό ελέγχου ταυτότητας που λάβατε."
    },
    "confirmCode": {
      "buttonLabel": "Επιβεβαίωση"
    },
    "SMSOTP": {
      "input": {
        "placeholder": "Εισάγετε τον κωδικό επαλήθευσης"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "Επανάληψη αποστολής σε {secondsPlaceholder} δευτερόλεπτα"
    }
  },
  "el-CY": {
    "notification": {
      "sendSMS": {
        "success": {
          "message": "Ο κωδικός επιβεβαίωσης στάλθηκε μέσω SMS στον {mobileNumberPlaceholder}.",
          "title": "Επιτυχημένη ενέργεια"
        },
        "asyncError": {
          "message": "Δεν ήταν δυνατή η αποστολή του μηνύματος. Ελέγξτε την καταχώρισή σας.",
          "title": "Σφάλμα"
        }
      }
    },
    "smsSent": {
      "notificationPersist": {
        "success": {
          "title": "Επιτυχημένη ενέργεια",
          "message": "Ο κωδικός ελέγχου ταυτότητας στάλθηκε μέσω SMS. Μπορεί να χρησιμοποιηθεί μόνο μία φορά και εντός περιορισμένου χρόνου για τη σύνδεση."
        }
      }
    },
    "SMSOTPForm": {
      "title": "Κωδικός ελέγχου ταυτότητας μέσω SMS"
    },
    "resendSMS": {
      "buttonLabel": "Στείλε ξανά"
    },
    "unlockWith": {
      "label": "Εισαγάγετε και επιβεβαιώστε τον κωδικό ελέγχου ταυτότητας που λάβατε."
    },
    "confirmCode": {
      "buttonLabel": "Επιβεβαίωση"
    },
    "SMSOTP": {
      "input": {
        "placeholder": "Εισάγετε τον κωδικό επαλήθευσης"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "Επανάληψη αποστολής σε {secondsPlaceholder} δευτερόλεπτα"
    }
  },
  "en-GB": {
    "notification": {
      "sendSMS": {
        "success": {
          "message": "The authentication code was sent via SMS to {mobileNumberPlaceholder}.",
          "title": "Successful Action"
        },
        "asyncError": {
          "message": "The message could not be sent. Please check your entry.",
          "title": "Error"
        }
      }
    },
    "smsSent": {
      "notificationPersist": {
        "success": {
          "title": "Successful action",
          "message": "The authentication code was sent via SMS. It can only be used once and within a limited time for logging in.."
        }
      }
    },
    "SMSOTPForm": {
      "title": "Authentication code by SMS"
    },
    "resendSMS": {
      "buttonLabel": "Resend"
    },
    "unlockWith": {
      "label": "Enter and confirm the authentication code received."
    },
    "confirmCode": {
      "buttonLabel": "Confirm"
    },
    "SMSOTP": {
      "input": {
        "placeholder": "Enter authentication code"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "Resend in {secondsPlaceholder} Seconds"
    }
  },
  "en-IE": {
    "notification": {
      "sendSMS": {
        "success": {
          "message": "The authentication code was sent via SMS to {mobileNumberPlaceholder}.",
          "title": "Successful Action"
        },
        "asyncError": {
          "title": "Error"
        }
      }
    },
    "smsSent": {
      "notificationPersist": {
        "success": {}
      }
    },
    "SMSOTPForm": {},
    "resendSMS": {
      "buttonLabel": "Resend"
    },
    "unlockWith": {
      "label": "Enter and confirm the authentication code received."
    },
    "confirmCode": {
      "buttonLabel": "Confirm"
    },
    "SMSOTP": {
      "input": {
        "placeholder": "Enter authentication code"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "Resend in {secondsPlaceholder} Seconds"
    }
  },
  "en-US": {
    "notification": {
      "sendSMS": {
        "success": {
          "message": "The authentication code was sent via SMS to {mobileNumberPlaceholder}.",
          "title": "Successful Action"
        },
        "asyncError": {
          "message": "The message could not be sent. Please check your entry.",
          "title": "Error"
        }
      }
    },
    "smsSent": {
      "notificationPersist": {
        "success": {
          "title": "Successful action",
          "message": "The authentication code was sent via SMS. It can only be used once and within a limited time for logging in.."
        }
      }
    },
    "SMSOTPForm": {
      "title": "Authentication code by SMS"
    },
    "resendSMS": {
      "buttonLabel": "Resend"
    },
    "unlockWith": {
      "label": "Enter and confirm the authentication code received."
    },
    "confirmCode": {
      "buttonLabel": "Confirm"
    },
    "SMSOTP": {
      "input": {
        "placeholder": "Enter authentication code"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "Resend in {secondsPlaceholder} Seconds"
    }
  },
  "es-ES": {
    "notification": {
      "sendSMS": {
        "success": {
          "message": "El código de autenticación se envió por SMS a {mobileNumberPlaceholder}.",
          "title": "Listo"
        },
        "asyncError": {
          "message": "El mensaje no se pudo enviar. Por favor revise su entrada.",
          "title": "Error"
        }
      }
    },
    "smsSent": {
      "notificationPersist": {
        "success": {
          "title": "Listo",
          "message": "El código de autenticación se envió por SMS. Solo se puede usar una vez y dentro de un tiempo limitado para iniciar sesión."
        }
      }
    },
    "SMSOTPForm": {
      "title": "Código de autenticación por SMS"
    },
    "resendSMS": {
      "buttonLabel": "Enviar de nuevo"
    },
    "unlockWith": {
      "label": "Introduzca y confirme el código de autenticación recibido."
    },
    "confirmCode": {
      "buttonLabel": "Confirmar"
    },
    "SMSOTP": {
      "input": {
        "placeholder": "Ingrese el código de autenticación"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "Volver a enviar en {secondsPlaceholder} segundos"
    }
  },
  "et-EE": {
    "notification": {
      "sendSMS": {
        "success": {
          "message": "Autentimiskood saadeti SMS-i teel {mobileNumberPlaceholder}.",
          "title": "Edukas tegevus"
        },
        "asyncError": {
          "message": "Sõnumit ei õnnestunud saata. Palun kontrollige oma sisestust.",
          "title": "Viga"
        }
      }
    },
    "smsSent": {
      "notificationPersist": {
        "success": {
          "title": "Edukas tegevus",
          "message": "Autentimiskood saadeti SMS-iga. Seda saab kasutada ainult üks kord ja piiratud aja jooksul sisselogimiseks."
        }
      }
    },
    "SMSOTPForm": {
      "title": "Autentimiskood SMS-i teel"
    },
    "resendSMS": {
      "buttonLabel": "Saada uuesti"
    },
    "unlockWith": {
      "label": "Sisestage ja kinnitage saadud autentimiskood."
    },
    "confirmCode": {
      "buttonLabel": "Kinnita"
    },
    "SMSOTP": {
      "input": {
        "placeholder": "Sisestage autentimiskood"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "Saada uuesti {secondsPlaceholder} sekundi pärast"
    }
  },
  "fi-FI": {
    "notification": {
      "sendSMS": {
        "success": {
          "message": "Vahvistuskoodi lähetettiin tekstiviestillä osoitteeseen {mobileNumberPlaceholder}.",
          "title": "Onnistunut toiminta"
        },
        "asyncError": {
          "message": "Viestiä ei voitu lähettää. Tarkista syöttösi.",
          "title": "Virhe"
        }
      }
    },
    "smsSent": {
      "notificationPersist": {
        "success": {
          "title": "Onnistunut toiminta",
          "message": "Todennuskoodi lähetettiin tekstiviestillä. Sitä voidaan käyttää vain kerran ja rajoitetun ajan sisällä kirjautumiseen."
        }
      }
    },
    "SMSOTPForm": {
      "title": "Todennuskoodi tekstiviestillä"
    },
    "resendSMS": {
      "buttonLabel": "Lähetä uudestaan"
    },
    "unlockWith": {
      "label": "Syötä ja vahvista vastaanotettu todennuskoodi."
    },
    "confirmCode": {
      "buttonLabel": "Vahvista"
    },
    "SMSOTP": {
      "input": {
        "placeholder": "Kirjoita tunnistekoodi"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "Lähetä uudelleen {secondsPlaceholder} sekunnin kuluttua"
    }
  },
  "fr-BE": {
    "notification": {
      "sendSMS": {
        "success": {
          "message": "Le code d'authentification a été envoyé par SMS à {mobileNumberPlaceholder}.",
          "title": "Action réussie"
        },
        "asyncError": {
          "message": "Le message n'a pas pu être envoyé. Veuillez vérifier votre saisie.",
          "title": "Erreur"
        }
      }
    },
    "smsSent": {
      "notificationPersist": {
        "success": {
          "title": "Action réussie",
          "message": "Le code d'authentification a été envoyé par SMS. Il ne peut être utilisé qu'une seule fois et dans un délai limité pour se connecter."
        }
      }
    },
    "SMSOTPForm": {
      "title": "Code d'authentification par SMS"
    },
    "resendSMS": {
      "buttonLabel": "Renvoyer"
    },
    "unlockWith": {
      "label": "Entrez et confirmez le code d'authentification reçu."
    },
    "confirmCode": {
      "buttonLabel": "Confirmer"
    },
    "SMSOTP": {
      "input": {
        "placeholder": "Entrez le code d'authentification"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "Renvoyer dans {secondsPlaceholder} secondes"
    }
  },
  "fr-CH": {
    "notification": {
      "sendSMS": {
        "success": {
          "message": "Le code d'authentification a été envoyé par SMS à {mobileNumberPlaceholder}.",
          "title": "Action réussie"
        },
        "asyncError": {
          "title": "Erreur"
        }
      }
    },
    "smsSent": {
      "notificationPersist": {
        "success": {}
      }
    },
    "SMSOTPForm": {},
    "resendSMS": {
      "buttonLabel": "Renvoyer"
    },
    "unlockWith": {
      "label": "Entrez et confirmez le code d'authentification reçu."
    },
    "confirmCode": {
      "buttonLabel": "Confirmer"
    },
    "SMSOTP": {
      "input": {
        "placeholder": "Saisir le code d'authentification"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "Renvoyer dans {secondsPlaceholder} secondes"
    }
  },
  "fr-FR": {
    "notification": {
      "sendSMS": {
        "success": {
          "message": "Le code d'authentification a été envoyé par SMS à {mobileNumberPlaceholder}.",
          "title": "Action réussie"
        },
        "asyncError": {
          "message": "Le message n'a pas pu être envoyé. Veuillez vérifier votre saisie.",
          "title": "Erreur"
        }
      }
    },
    "smsSent": {
      "notificationPersist": {
        "success": {
          "title": "Action réussie",
          "message": "Le code d'authentification a été envoyé par SMS. Il ne peut être utilisé qu'une seule fois et dans un délai limité pour se connecter."
        }
      }
    },
    "SMSOTPForm": {
      "title": "Code d'authentification par SMS"
    },
    "resendSMS": {
      "buttonLabel": "Envoyer à nouveau"
    },
    "unlockWith": {
      "label": "Entrez et confirmez le code d'authentification reçu."
    },
    "confirmCode": {
      "buttonLabel": "Confirmer"
    },
    "SMSOTP": {
      "input": {
        "placeholder": "Saisir le code d'authentification"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "Renvoyer dans {secondsPlaceholder} secondes"
    }
  },
  "fr-LU": {
    "notification": {
      "sendSMS": {
        "success": {
          "message": "Le code d'authentification a été envoyé par SMS à {mobileNumberPlaceholder}.",
          "title": "Action réussie"
        },
        "asyncError": {
          "message": "Le message n'a pas pu être envoyé. Veuillez vérifier votre saisie.",
          "title": "Erreur"
        }
      }
    },
    "smsSent": {
      "notificationPersist": {
        "success": {
          "title": "Action réussie",
          "message": "Le code d'authentification a été envoyé par SMS. Il ne peut être utilisé qu'une seule fois et dans un délai limité pour se connecter."
        }
      }
    },
    "SMSOTPForm": {
      "title": "Code d'authentification par SMS"
    },
    "resendSMS": {
      "buttonLabel": "Renvoyer"
    },
    "unlockWith": {
      "label": "Entrez et confirmez le code d'authentification reçu."
    },
    "confirmCode": {
      "buttonLabel": "Confirmer"
    },
    "SMSOTP": {
      "input": {
        "placeholder": "Saisir le code d'authentification"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "Renvoyer dans {secondsPlaceholder} secondes"
    }
  },
  "ga-IE": {
    "notification": {
      "sendSMS": {
        "success": {
          "message": "Seoladh cód deimhnithe via SMS go {mobileNumberPlaceholder}.",
          "title": "Gníomh rathúil"
        },
        "asyncError": {
          "message": "Ní fhéadfaí an teachtaireacht a sheoladh. Seiceáil d’iontráil le do thoil.",
          "title": "Earráid"
        }
      }
    },
    "smsSent": {
      "notificationPersist": {
        "success": {
          "title": "Gníomh rathúil",
          "message": "Seoladh an cód fíordheimhnithe trí SMS. Ní féidir é a úsáid ach uair amháin agus laistigh de thréimhse teoranta chun logáil isteach."
        }
      }
    },
    "SMSOTPForm": {
      "title": "Cód fíordheimhnithe trí SMS"
    },
    "resendSMS": {
      "buttonLabel": "Seol arís"
    },
    "unlockWith": {
      "label": "Cuir isteach agus deimhnigh an cód fíordheimhnithe a fuarthas."
    },
    "confirmCode": {
      "buttonLabel": "Deimhnigh"
    },
    "SMSOTP": {
      "input": {
        "placeholder": "Cuir isteach cód fíordheimhnithe"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "Athseoladh i {secondsPlaceholder} soicind"
    }
  },
  "he-IL": {
    "notification": {
      "sendSMS": {
        "success": {
          "message": "קוד האימות נשלח באמצעות הודעת טקסט אל {mobileNumberPlaceholder}.",
          "title": "פעולה מוצלחת"
        },
        "asyncError": {
          "message": "לא ניתן היה לשלוח את ההודעה. אנא בדוק את הערך שלך.",
          "title": "שְׁגִיאָה"
        }
      }
    },
    "smsSent": {
      "notificationPersist": {
        "success": {
          "title": "פעולה מוצלחת",
          "message": "קוד האימות נשלח באמצעות SMS. ניתן להשתמש בו רק פעם אחת ובתוך זמן מוגבל לכניסה."
        }
      }
    },
    "SMSOTPForm": {
      "title": "קוד אימות באמצעות SMS"
    },
    "resendSMS": {
      "buttonLabel": "שלח שוב"
    },
    "unlockWith": {
      "label": "הזן ואשר את קוד האימות שהתקבל."
    },
    "confirmCode": {
      "buttonLabel": "לְאַשֵׁר"
    },
    "SMSOTP": {
      "input": {
        "placeholder": "הזן קוד אימות"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "שלח מחדש בעוד {secondsPlaceholder} שניות"
    }
  },
  "hi-IN": {
    "notification": {
      "sendSMS": {
        "success": {
          "message": "प्रमाणीकरण कोड को {mobileNumberPlaceholder} को एसएमएस के माध्यम से भेजा गया था।",
          "title": "सफल कार्रवाई"
        },
        "asyncError": {
          "title": "त्रुटि"
        }
      }
    },
    "smsSent": {
      "notificationPersist": {
        "success": {}
      }
    },
    "SMSOTPForm": {},
    "resendSMS": {
      "buttonLabel": "फिर से भेजें"
    },
    "unlockWith": {
      "label": "प्राप्त प्रमाणीकरण कोड दर्ज करें और पुष्टि करें।"
    },
    "confirmCode": {
      "buttonLabel": "पुष्टि करें"
    },
    "SMSOTP": {
      "input": {
        "placeholder": "प्रमाणीकरण कोड दर्ज करें"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "{secondsPlaceholder} सेकंड में पुन: भेजें"
    }
  },
  "hr-HR": {
    "notification": {
      "sendSMS": {
        "success": {
          "message": "Autentifikacijski kod je poslan putem SMS-a na {mobileNumberPlaceholder}.",
          "title": "Uspješna akcija"
        },
        "asyncError": {
          "message": "Poruka se nije mogla poslati. Molimo provjerite svoj unos.",
          "title": "Pogreška"
        }
      }
    },
    "smsSent": {
      "notificationPersist": {
        "success": {
          "title": "Uspješna akcija",
          "message": "Kod za autentifikaciju poslan je putem SMS-a. Može se koristiti samo jednom i unutar ograničenog vremena za prijavu."
        }
      }
    },
    "SMSOTPForm": {
      "title": "Autentifikacijski kod putem SMS-a"
    },
    "resendSMS": {
      "buttonLabel": "Pošalji opet"
    },
    "unlockWith": {
      "label": "Unesite i potvrdite primljeni autentifikacijski kod."
    },
    "confirmCode": {
      "buttonLabel": "Potvrda"
    },
    "SMSOTP": {
      "input": {
        "placeholder": "Unesite autentifikacijski kod"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "Ponovno pošalji za {secondsPlaceholder} sekundi"
    }
  },
  "hu-HU": {
    "notification": {
      "sendSMS": {
        "success": {
          "message": "Az hitelesítő kód SMS-ben lett elküldve a {mobileNumberPlaceholder} számra.",
          "title": "Sikeres művelet"
        },
        "asyncError": {
          "message": "Az üzenetet nem sikerült elküldeni. Kérjük, ellenőrizze a bejegyzést.",
          "title": "Hiba"
        }
      }
    },
    "smsSent": {
      "notificationPersist": {
        "success": {
          "title": "Sikeres művelet",
          "message": "A hitelesítési kódot SMS-ben küldték el. Csak egyszer és korlátozott ideig használható a bejelentkezéshez."
        }
      }
    },
    "SMSOTPForm": {
      "title": "Hitelesítési kód SMS-ben"
    },
    "resendSMS": {
      "buttonLabel": "Küldd újra"
    },
    "unlockWith": {
      "label": "Írja be és erősítse meg a kapott hitelesítési kódot."
    },
    "confirmCode": {
      "buttonLabel": "Megerősít"
    },
    "SMSOTP": {
      "input": {
        "placeholder": "Adja meg az hitelesítő kódot"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "Újraküldés {secondsPlaceholder} másodperc múlva"
    }
  },
  "it-CH": {
    "notification": {
      "sendSMS": {
        "success": {
          "message": "Il codice di autenticazione è stato inviato tramite SMS a {mobileNumberPlaceholder}.",
          "title": "Azione di successo"
        },
        "asyncError": {
          "title": "Errore"
        }
      }
    },
    "smsSent": {
      "notificationPersist": {
        "success": {}
      }
    },
    "SMSOTPForm": {},
    "resendSMS": {
      "buttonLabel": "Invia di nuovo"
    },
    "unlockWith": {
      "label": "Inserisci e conferma il codice di autenticazione ricevuto."
    },
    "confirmCode": {
      "buttonLabel": "Confermare"
    },
    "SMSOTP": {
      "input": {
        "placeholder": "Inserire il codice di autenticazione"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "Invia di nuovo fra {secondsPlaceholder} secondi"
    }
  },
  "it-IT": {
    "notification": {
      "sendSMS": {
        "success": {
          "message": "Il codice di autenticazione è stato inviato tramite SMS a {mobileNumberPlaceholder}.",
          "title": "Azione riuscita"
        },
        "asyncError": {
          "message": "Impossibile inviare il messaggio. Si prega di controllare la tua voce.",
          "title": "Errore"
        }
      }
    },
    "smsSent": {
      "notificationPersist": {
        "success": {
          "title": "Azione riuscita",
          "message": "Il codice di autenticazione è stato inviato tramite SMS. Può essere utilizzato solo una volta ed entro un tempo limitato per l'accesso."
        }
      }
    },
    "SMSOTPForm": {
      "title": "Codice di autenticazione via SMS"
    },
    "resendSMS": {
      "buttonLabel": "Invia di nuovo"
    },
    "unlockWith": {
      "label": "Inserisci e conferma il codice di autenticazione ricevuto."
    },
    "confirmCode": {
      "buttonLabel": "Conferma"
    },
    "SMSOTP": {
      "input": {
        "placeholder": "Inserire il codice di autenticazione"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "Invia di nuovo fra {secondsPlaceholder} secondi"
    }
  },
  "ja-JP": {
    "notification": {
      "sendSMS": {
        "success": {
          "message": "認証コードは、SMSで{mobileNumberPlaceholder}に送信されました。",
          "title": "成功したアクション"
        },
        "asyncError": {
          "message": "メッセージを送信できませんでした。 入力内容をご確認ください。",
          "title": "エラー"
        }
      }
    },
    "smsSent": {
      "notificationPersist": {
        "success": {
          "title": "成功したアクション",
          "message": "認証コードは SMS 経由で送信されました。 ログイン時に1回限り、限られた時間内でのみ使用できます。"
        }
      }
    },
    "SMSOTPForm": {
      "title": "SMS経由の認証コード"
    },
    "resendSMS": {
      "buttonLabel": "もう一度送信"
    },
    "unlockWith": {
      "label": "受信した認証コードを入力して確認します。"
    },
    "confirmCode": {
      "buttonLabel": "確認"
    },
    "SMSOTP": {
      "input": {
        "placeholder": "認証コードを入力してください"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "{secondsPlaceholder} 秒後に再送信"
    }
  },
  "ko-KR": {
    "notification": {
      "sendSMS": {
        "success": {
          "message": "인증 코드가 {mobileNumberPlaceholder}에게 SMS로 전송되었습니다.",
          "title": "성공적인 액션"
        },
        "asyncError": {
          "title": "오류"
        }
      }
    },
    "smsSent": {
      "notificationPersist": {
        "success": {}
      }
    },
    "SMSOTPForm": {},
    "resendSMS": {
      "buttonLabel": "다시 보내기"
    },
    "unlockWith": {
      "label": "받은 인증 코드를 입력하고 확인하십시오."
    },
    "confirmCode": {
      "buttonLabel": "확인"
    },
    "SMSOTP": {
      "input": {
        "placeholder": "인증 코드를 입력하세요"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "{secondsPlaceholder}초 후에 재전송"
    }
  },
  "lt-LT": {
    "notification": {
      "sendSMS": {
        "success": {
          "message": "Autentifikavimo kodas buvo išsiųstas SMS žinute į {mobileNumberPlaceholder}.",
          "title": "Sėkmingas veiksmas"
        },
        "asyncError": {
          "message": "Nepavyko išsiųsti pranešimo. Patikrinkite savo įrašą.",
          "title": "Klaida"
        }
      }
    },
    "smsSent": {
      "notificationPersist": {
        "success": {
          "title": "Sėkmingas veiksmas",
          "message": "Autentifikavimo kodas buvo išsiųstas SMS žinute. Jį galima naudoti tik vieną kartą ir per ribotą prisijungimo laiką."
        }
      }
    },
    "SMSOTPForm": {
      "title": "Autentifikavimo kodas SMS žinute"
    },
    "resendSMS": {
      "buttonLabel": "Siųsti dar kartą"
    },
    "unlockWith": {
      "label": "Įveskite ir patvirtinkite gautą autentifikavimo kodą."
    },
    "confirmCode": {
      "buttonLabel": "Patvirtinti"
    },
    "SMSOTP": {
      "input": {
        "placeholder": "Įveskite autentifikacijos kodą"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "Persiųsti po {secondsPlaceholder} sekundžių"
    }
  },
  "lv-LV": {
    "notification": {
      "sendSMS": {
        "success": {
          "message": "Autentifikācijas kods tika nosūtīts caur SMS uz {mobileNumberPlaceholder}.",
          "title": "Veiksmīga darbība"
        },
        "asyncError": {
          "message": "Ziņojumu nevarēja nosūtīt. Lūdzu, pārbaudiet savu ierakstu.",
          "title": "Kļūda"
        }
      }
    },
    "smsSent": {
      "notificationPersist": {
        "success": {
          "title": "Veiksmīga darbība",
          "message": "Autentifikācijas kods tika nosūtīts ar SMS. To var izmantot tikai vienu reizi un ierobežotā laikā, lai pieteiktos."
        }
      }
    },
    "SMSOTPForm": {
      "title": "Autentifikācijas kods, izmantojot īsziņu"
    },
    "resendSMS": {
      "buttonLabel": "Sūtīt vēlreiz"
    },
    "unlockWith": {
      "label": "Ievadiet un apstipriniet saņemto autentifikācijas kodu."
    },
    "confirmCode": {
      "buttonLabel": "Apstiprināt"
    },
    "SMSOTP": {
      "input": {
        "placeholder": "Ievadiet autentifikācijas kodu"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "Atkārtoti nosūtīt pēc {secondsPlaceholder} sekundēm"
    }
  },
  "mt-MT": {
    "notification": {
      "sendSMS": {
        "success": {
          "message": "Il-kodiċi ta' awtentikazzjoni ġie mibgħut permezz ta' SMS lil {mobileNumberPlaceholder}.",
          "title": "Azzjoni ta' suċċess"
        },
        "asyncError": {
          "message": "Il-messaġġ ma setax jintbagħat. Jekk jogħġbok iċċekkja d-dħul tiegħek.",
          "title": "Żball"
        }
      }
    },
    "smsSent": {
      "notificationPersist": {
        "success": {
          "title": "Azzjoni ta' suċċess",
          "message": "Il-kodiċi ta' awtentikazzjoni intbagħat permezz ta' SMS. Jista 'jintuża darba biss u fi żmien limitat għall-illoggjar."
        }
      }
    },
    "SMSOTPForm": {
      "title": "Kodiċi ta 'awtentikazzjoni permezz ta' SMS"
    },
    "resendSMS": {
      "buttonLabel": "Ibgħat mill-ġdid"
    },
    "unlockWith": {
      "label": "Daħħal u kkonferma l-kodiċi ta 'awtentikazzjoni riċevut."
    },
    "confirmCode": {
      "buttonLabel": "Ikkonferma"
    },
    "SMSOTP": {
      "input": {
        "placeholder": "Idħol il-kodiċi ta' awtentikazzjoni"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "Ibgħat mill-ġdid fi {secondsPlaceholder} sekondi"
    }
  },
  "nb-NO": {
    "notification": {
      "sendSMS": {
        "success": {
          "message": "Autentiseringskoden ble sendt via SMS til {mobileNumberPlaceholder}.",
          "title": "Vellykket handling"
        },
        "asyncError": {
          "title": "Feil"
        }
      }
    },
    "smsSent": {
      "notificationPersist": {
        "success": {}
      }
    },
    "SMSOTPForm": {},
    "resendSMS": {
      "buttonLabel": "Send igjen"
    },
    "unlockWith": {
      "label": "Skriv inn og bekreft autentiseringskoden du mottok."
    },
    "confirmCode": {
      "buttonLabel": "Bekrefte"
    },
    "SMSOTP": {
      "input": {
        "placeholder": "Skriv inn godkjenningskode"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "Send på nytt om {secondsPlaceholder} sekunder"
    }
  },
  "nl-BE": {
    "notification": {
      "sendSMS": {
        "success": {
          "message": "De authenticatiecode werd via SMS naar {mobileNumberPlaceholder} gestuurd.",
          "title": "Geslaagde actie"
        },
        "asyncError": {
          "title": "Fout"
        }
      }
    },
    "smsSent": {
      "notificationPersist": {
        "success": {}
      }
    },
    "SMSOTPForm": {},
    "resendSMS": {
      "buttonLabel": "Opnieuw verzenden"
    },
    "unlockWith": {
      "label": "Voer de ontvangen authenticatiecode in en bevestig deze."
    },
    "confirmCode": {
      "buttonLabel": "Bevestigen"
    },
    "SMSOTP": {
      "input": {
        "placeholder": "Voer de authenticatiecode in"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "Opnieuw verzenden over {secondsPlaceholder} seconden"
    }
  },
  "nl-NL": {
    "notification": {
      "sendSMS": {
        "success": {
          "message": "De authenticatiecode werd via SMS naar {mobileNumberPlaceholder} gestuurd.",
          "title": "Geslaagde actie"
        },
        "asyncError": {
          "message": "Het bericht kon niet worden verzonden. Controleer uw invoer.",
          "title": "Fout"
        }
      }
    },
    "smsSent": {
      "notificationPersist": {
        "success": {
          "title": "Geslaagde actie",
          "message": "De authenticatiecode is per sms verzonden. Het kan slechts één keer en binnen een beperkte tijd worden gebruikt om in te loggen."
        }
      }
    },
    "SMSOTPForm": {
      "title": "Authenticatiecode via sms"
    },
    "resendSMS": {
      "buttonLabel": "Opnieuw verzenden"
    },
    "unlockWith": {
      "label": "Voer de ontvangen authenticatiecode in en bevestig deze."
    },
    "confirmCode": {
      "buttonLabel": "Bevestigen"
    },
    "SMSOTP": {
      "input": {
        "placeholder": "Voer de authenticatiecode in"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "Opnieuw verzenden over {secondsPlaceholder} seconden"
    }
  },
  "pl-PL": {
    "notification": {
      "sendSMS": {
        "success": {
          "message": "Kod autoryzacyjny został wysłany za pośrednictwem SMS na {mobileNumberPlaceholder}.",
          "title": "Czynność zakończona powodzeniem"
        },
        "asyncError": {
          "message": "Nie udało się wysłać wiadomości. Sprawdź swój wpis.",
          "title": "Błąd"
        }
      }
    },
    "smsSent": {
      "notificationPersist": {
        "success": {
          "title": "Czynność zakończona powodzeniem",
          "message": "Kod uwierzytelniający został wysłany SMS-em. Można go użyć tylko raz iw ograniczonym czasie do zalogowania."
        }
      }
    },
    "SMSOTPForm": {
      "title": "Kod uwierzytelniający przez SMS"
    },
    "resendSMS": {
      "buttonLabel": "Wyślij ponownie"
    },
    "unlockWith": {
      "label": "Wprowadź i potwierdź otrzymany kod uwierzytelniający."
    },
    "confirmCode": {
      "buttonLabel": "Potwierdź"
    },
    "SMSOTP": {
      "input": {
        "placeholder": "Wprowadź kod uwierzytelniania"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "Wyślij ponownie za {secondsPlaceholder} sekund"
    }
  },
  "pt-PT": {
    "notification": {
      "sendSMS": {
        "success": {
          "message": "O código de autenticação foi enviado por SMS para {mobileNumberPlaceholder}.",
          "title": "Ação bem-sucedida"
        },
        "asyncError": {
          "message": "A mensagem não pôde ser enviada. Por favor, verifique sua entrada.",
          "title": "Erro"
        }
      }
    },
    "smsSent": {
      "notificationPersist": {
        "success": {
          "title": "Ação bem-sucedida",
          "message": "O código de autenticação foi enviado via SMS. Ele só pode ser usado uma vez e dentro de um tempo limitado para fazer login."
        }
      }
    },
    "SMSOTPForm": {
      "title": "Código de autenticação via SMS"
    },
    "resendSMS": {
      "buttonLabel": "Envie novamente"
    },
    "unlockWith": {
      "label": "Digite e confirme o código de autenticação recebido."
    },
    "confirmCode": {
      "buttonLabel": "Confirmar"
    },
    "SMSOTP": {
      "input": {
        "placeholder": "Insira o código de autenticação"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "Reenviar em {secondsPlaceholder} segundos"
    }
  },
  "ro-RO": {
    "notification": {
      "sendSMS": {
        "success": {
          "message": "Codul de autentificare a fost trimis prin SMS la {mobileNumberPlaceholder}.",
          "title": "Acțiune reușită"
        },
        "asyncError": {
          "message": "Mesajul nu a putut fi trimis. Vă rugăm să verificați intrarea.",
          "title": "Eroare"
        }
      }
    },
    "smsSent": {
      "notificationPersist": {
        "success": {
          "title": "Acțiune reușită",
          "message": "Codul de autentificare a fost trimis prin SMS. Poate fi folosit o singură dată și într-un timp limitat pentru autentificare."
        }
      }
    },
    "SMSOTPForm": {
      "title": "Cod de autentificare prin SMS"
    },
    "resendSMS": {
      "buttonLabel": "Trimite din nou"
    },
    "unlockWith": {
      "label": "Introduceți și confirmați codul de autentificare primit."
    },
    "confirmCode": {
      "buttonLabel": "Confirmare"
    },
    "SMSOTP": {
      "input": {
        "placeholder": "Introduceți codul de autentificare"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "Retrimite în {secondsPlaceholder} secunde"
    }
  },
  "ru-RU": {
    "notification": {
      "sendSMS": {
        "success": {
          "message": "Код аутентификации был отправлен по SMS на {mobileNumberPlaceholder}.",
          "title": "Успешное действие"
        },
        "asyncError": {
          "message": "Сообщение не может быть отправлено. Пожалуйста, проверьте вашу запись.",
          "title": "Ошибка"
        }
      }
    },
    "smsSent": {
      "notificationPersist": {
        "success": {
          "title": "Успешное действие",
          "message": "Код аутентификации был отправлен в SMS. Его можно использовать только один раз и в течение ограниченного времени для входа в систему."
        }
      }
    },
    "SMSOTPForm": {
      "title": "Код аутентификации по SMS"
    },
    "resendSMS": {
      "buttonLabel": "Отправь еще раз"
    },
    "unlockWith": {
      "label": "Введите и подтвердите полученный код аутентификации."
    },
    "confirmCode": {
      "buttonLabel": "Подтвердить"
    },
    "SMSOTP": {
      "input": {
        "placeholder": "Введите код аутентификации"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "Отправить снова через {secondsPlaceholder} секунд"
    }
  },
  "sk-SK": {
    "notification": {
      "sendSMS": {
        "success": {
          "message": "Autentifikačný kód bol odoslaný pomocou SMS na {mobileNumberPlaceholder}.",
          "title": "Úspešná akcia"
        },
        "asyncError": {
          "message": "Správu sa nepodarilo odoslať. Skontrolujte svoje zadanie.",
          "title": "Chyba"
        }
      }
    },
    "smsSent": {
      "notificationPersist": {
        "success": {
          "title": "Úspešná akcia",
          "message": "Overovací kód bol odoslaný prostredníctvom SMS. Dá sa použiť len raz a v obmedzenom čase na prihlásenie."
        }
      }
    },
    "SMSOTPForm": {
      "title": "Autentifikačný kód prostredníctvom SMS"
    },
    "resendSMS": {
      "buttonLabel": "Poslať znova"
    },
    "unlockWith": {
      "label": "Zadajte a potvrďte prijatý overovací kód."
    },
    "confirmCode": {
      "buttonLabel": "Potvrdiť"
    },
    "SMSOTP": {
      "input": {
        "placeholder": "Zadajte overovací kód"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "Znovu poslať o {secondsPlaceholder} sekundy"
    }
  },
  "sl-SI": {
    "notification": {
      "sendSMS": {
        "success": {
          "message": "Avtentikacijska koda je bila poslana preko SMS na {mobileNumberPlaceholder}.",
          "title": "Uspešno opravljeno"
        },
        "asyncError": {
          "message": "Sporočila ni bilo mogoče poslati. Prosimo, preverite svoj vnos.",
          "title": "Napaka"
        }
      }
    },
    "smsSent": {
      "notificationPersist": {
        "success": {
          "title": "Uspešno opravljeno",
          "message": "Koda za preverjanje pristnosti je bila poslana prek SMS-a. Uporabite ga lahko samo enkrat in v omejenem času za prijavo."
        }
      }
    },
    "SMSOTPForm": {
      "title": "Koda za preverjanje pristnosti prek SMS-a"
    },
    "resendSMS": {
      "buttonLabel": "Pošlji ponovno"
    },
    "unlockWith": {
      "label": "Vnesite in potrdite prejeto kodo za preverjanje pristnosti."
    },
    "confirmCode": {
      "buttonLabel": "Potrdi"
    },
    "SMSOTP": {
      "input": {
        "placeholder": "Vnesite kodo za preverjanje pristnosti"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "Ponovno pošlji čez {secondsPlaceholder} sekund"
    }
  },
  "sv-SE": {
    "notification": {
      "sendSMS": {
        "success": {
          "message": "Autentiseringskoden skickades via SMS till {mobileNumberPlaceholder}.",
          "title": "Åtgärd utförd"
        },
        "asyncError": {
          "message": "Meddelandet kunde inte skickas. Kontrollera ditt bidrag.",
          "title": "Fel"
        }
      }
    },
    "smsSent": {
      "notificationPersist": {
        "success": {
          "title": "Åtgärd utförd",
          "message": "Autentiseringskoden skickades via SMS. Den kan endast användas en gång och inom en begränsad tid för inloggning."
        }
      }
    },
    "SMSOTPForm": {
      "title": "Autentiseringskod via SMS"
    },
    "resendSMS": {
      "buttonLabel": "Skicka igen"
    },
    "unlockWith": {
      "label": "Ange och bekräfta den mottagna autentiseringskoden."
    },
    "confirmCode": {
      "buttonLabel": "Bekräfta"
    },
    "SMSOTP": {
      "input": {
        "placeholder": "Ange autentiseringskod"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "Skicka igen om {secondsPlaceholder} sekunder"
    }
  },
  "th-TH": {
    "notification": {
      "sendSMS": {
        "success": {
          "message": "รหัสการตรวจสอบถูกส่งผ่าน SMS ไปยัง {mobileNumberPlaceholder}",
          "title": "การดำเนินการที่ประสบความสำเร็จ"
        },
        "asyncError": {
          "title": "ความผิดพลาด"
        }
      }
    },
    "smsSent": {
      "notificationPersist": {
        "success": {}
      }
    },
    "SMSOTPForm": {},
    "resendSMS": {
      "buttonLabel": "ส่งอีกครั้ง"
    },
    "unlockWith": {
      "label": "ป้อนและยืนยันรหัสการรับรองที่ได้รับ"
    },
    "confirmCode": {
      "buttonLabel": "ยืนยัน"
    },
    "SMSOTP": {
      "input": {
        "placeholder": "ป้อนรหัสยืนยัน"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "ส่งใหม่ใน {secondsPlaceholder} วินาที"
    }
  },
  "tr-CY": {
    "notification": {
      "sendSMS": {
        "success": {
          "message": "Kimlik doğrulama kodu, SMS ile {mobileNumberPlaceholder}'a gönderildi.",
          "title": "Başarılı eylem"
        },
        "asyncError": {
          "message": "Mesaj gönderilemedi. Lütfen girişinizi kontrol edin.",
          "title": "Hata"
        }
      }
    },
    "smsSent": {
      "notificationPersist": {
        "success": {
          "title": "Başarılı eylem",
          "message": "Kimlik doğrulama kodu SMS ile gönderildi. Giriş yapmak için yalnızca bir kez ve sınırlı bir süre içinde kullanılabilir."
        }
      }
    },
    "SMSOTPForm": {
      "title": "SMS ile doğrulama kodu"
    },
    "resendSMS": {
      "buttonLabel": "Tekrar gönderin"
    },
    "unlockWith": {
      "label": "Alınan doğrulama kodunu girin ve onaylayın."
    },
    "confirmCode": {
      "buttonLabel": "Onayla"
    },
    "SMSOTP": {
      "input": {
        "placeholder": "Doğrulama kodunu girin"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "{secondsPlaceholder} saniye sonra yeniden gönder"
    }
  },
  "tr-TR": {
    "notification": {
      "sendSMS": {
        "success": {
          "message": "Kimlik doğrulama kodu, SMS ile {mobileNumberPlaceholder}'a gönderildi.",
          "title": "Başarılı eylem"
        },
        "asyncError": {
          "message": "Mesaj gönderilemedi. Lütfen girişinizi kontrol edin.",
          "title": "Hata"
        }
      }
    },
    "smsSent": {
      "notificationPersist": {
        "success": {
          "title": "Başarılı eylem",
          "message": "Kimlik doğrulama kodu SMS ile gönderildi. Giriş yapmak için yalnızca bir kez ve sınırlı bir süre içinde kullanılabilir."
        }
      }
    },
    "SMSOTPForm": {
      "title": "SMS ile doğrulama kodu"
    },
    "resendSMS": {
      "buttonLabel": "Tekrar gönderin"
    },
    "unlockWith": {
      "label": "Alınan doğrulama kodunu girin ve onaylayın."
    },
    "confirmCode": {
      "buttonLabel": "Onayla"
    },
    "SMSOTP": {
      "input": {
        "placeholder": "Doğrulama kodunu girin"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "{secondsPlaceholder} saniye sonra yeniden gönder"
    }
  },
  "uk-UA": {
    "notification": {
      "sendSMS": {
        "success": {
          "message": "Код автентифікації був відправлений через SMS на {mobileNumberPlaceholder}.",
          "title": "Успішна дія"
        },
        "asyncError": {
          "message": "Die Nachricht konnte nicht gesendet werden. Bitte prüfe deine Eingabe.",
          "title": "Помилка"
        }
      }
    },
    "smsSent": {
      "notificationPersist": {
        "success": {
          "title": "Успішна акція",
          "message": "Код автентифікації надіслано через SMS. Його можна використовувати лише один раз і протягом обмеженого часу для входу."
        }
      }
    },
    "SMSOTPForm": {
      "title": "Код автентифікації через SMS"
    },
    "resendSMS": {
      "buttonLabel": "Надіслати знову"
    },
    "unlockWith": {
      "label": "Введіть і підтвердьте отриманий код автентифікації."
    },
    "confirmCode": {
      "buttonLabel": "Підтвердити"
    },
    "SMSOTP": {
      "input": {
        "placeholder": "Введіть код автентифікації"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "Надіслати знову за {secondsPlaceholder} секунд"
    }
  },
  "vi-VN": {
    "notification": {
      "sendSMS": {
        "success": {
          "message": "Mã xác thực đã được gửi qua SMS đến {mobileNumberPlaceholder}.",
          "title": "Hành động thành công"
        },
        "asyncError": {
          "title": "Lỗi"
        }
      }
    },
    "smsSent": {
      "notificationPersist": {
        "success": {}
      }
    },
    "SMSOTPForm": {},
    "resendSMS": {
      "buttonLabel": "Gửi lại"
    },
    "unlockWith": {
      "label": "Nhập và xác nhận mã xác thực đã nhận."
    },
    "confirmCode": {
      "buttonLabel": "Xác nhận"
    },
    "SMSOTP": {
      "input": {
        "placeholder": "Nhập mã xác thực"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "Gửi lại sau {secondsPlaceholder} giây"
    }
  },
  "zh-CN": {
    "notification": {
      "sendSMS": {
        "success": {
          "message": "身份验证代码已通过短信发送至{mobileNumberPlaceholder}。",
          "title": "操作成功"
        },
        "asyncError": {
          "message": "无法发送消息。 请检查您的输入。",
          "title": "错误"
        }
      }
    },
    "smsSent": {
      "notificationPersist": {
        "success": {
          "title": "操作成功",
          "message": "验证码是通过短信发送的。 仅限登录一次，限时使用。"
        }
      }
    },
    "SMSOTPForm": {
      "title": "通过短信验证码"
    },
    "resendSMS": {
      "buttonLabel": "重新发送"
    },
    "unlockWith": {
      "label": "输入并确认收到的验证码。"
    },
    "confirmCode": {
      "buttonLabel": "确认"
    },
    "SMSOTP": {
      "input": {
        "placeholder": "请输入认证码"
      }
    },
    "resendSMSInSeconds": {
      "buttonLabel": "{secondsPlaceholder} 秒后重新发送"
    }
  }
}
</i18n>
